import { type AuditFinding, type AuditObjectAuditView, type AuditScore, formatOtsDateTime } from "@ots-frontend/ots-data";

/**
 * A data check the ticket failed outside the audit, such as a driver activity warning: WaterGo's `Warning`
 * (`type`, `message`, `priority`). Shown under Data Checks unless an active finding already covers it.
 */
export type AuditWarning = {
  type: string;
  message?: string;
  priority?: string;
};

/** "Oct 5, 15:58" (24-hour, as the operational grids show times) in the given time zone, or a dash. */
export const formatAuditDateTime = (value?: number, timeZone?: string): string =>
  value == null ? "—" : formatOtsDateTime(value, "short", { timeZone });

/**
 * Which audit rules stand for which driver-activity warnings. WaterGo's `DriverActivityAuditWarnings`:
 * a warning already represented by an active finding is folded into it rather than listed twice.
 */
const AUDIT_RULE_KEYS: Record<string, string[]> = {
  QUANTITY_MISMATCH: ["tta.quantity_mismatch"],
  DRIVER_MISMATCH: ["tta.driver_mismatch"],
  VEHICLE_MISMATCH: ["tta.vehicle_mismatch"],
  TRAILER_MISMATCH: ["tta.trailer_mismatch"],
  SERVICE_TYPE_MISMATCH: ["tta.service_type_mismatch"],
  TTA_NO_OPERATOR: ["tta.no_operator"],
  TTA_DELIVERY_TO_NON_PRIMARY_SITE: ["tta.delivery_assigned_non_primary_site", "tta.delivery_unassigned_site"],
  TRANSPORT_BSW_VALUE: ["tta.pickup_bsw_above_maximum"],
  TRANSPORT_OBS_GRAVITY_VALUE: ["tta.pickup_observed_gravity_out_of_range"],
  TRANSPORT_OBS_TEMP_VALUE: ["tta.pickup_observed_temperature_out_of_range"],
};

/** Warnings whose rule depends on which side of the ticket the message is about. */
const SIDED_RULE_KEYS: Record<string, [pickup: string, delivery: string]> = {
  DEFAULT_TANK_USAGE: ["tta.pickup_default_container_usage", "tta.delivery_default_container_usage"],
  TRANSPORT_SCADA_QUANTITY: ["tta.pickup_scada_quantity_mismatch", "tta.delivery_scada_quantity_mismatch"],
};

const ruleKeysFor = (warning: AuditWarning): string[] | undefined => {
  const sided = SIDED_RULE_KEYS[warning.type];
  if (!sided) return AUDIT_RULE_KEYS[warning.type];
  const message = warning.message?.toLowerCase() ?? "";
  const pickup = message.includes("pickup");
  const delivery = message.includes("delivery");
  return pickup && !delivery ? [sided[0]] : delivery && !pickup ? [sided[1]] : sided;
};

/** Splits activity warnings into those an active audit finding already covers and those it does not. */
export const partitionAuditWarnings = (warnings: AuditWarning[] | undefined, findings: AuditFinding[] | undefined) => {
  const active = new Set((findings ?? []).map((finding) => finding.ruleKey));
  const remaining: AuditWarning[] = [];
  const covered: AuditWarning[] = [];
  for (const warning of warnings ?? []) {
    (active.size > 0 && ruleKeysFor(warning)?.some((key) => active.has(key)) ? covered : remaining).push(warning);
  }
  return { remaining, covered };
};

/** The scores and findings to show for a ticket: the latest run's when it has them, as WaterGo's panel does. */
export const latestAuditResult = (view: AuditObjectAuditView | undefined): { scores: AuditScore[]; findings: AuditFinding[] } => ({
  scores: view?.latestRun?.scores?.length ? view.latestRun.scores : view?.scores ?? [],
  findings: view?.latestRun?.findings?.length ? view.latestRun.findings : view?.findings ?? [],
});

/** A score was held down by a rule's cap: it sits below what the weighted rules came to. */
export const isScoreCapped = (score: AuditScore): boolean =>
  score.confidence != null && score.uncappedConfidence != null
  && score.confidence + 0.000001 < score.uncappedConfidence && !!score.appliedCaps?.length;

/** `tta.quantity_mismatch` → `tta quantity mismatch`, as the WaterGo dialog shows rule keys. */
export const auditDisplayName = (value?: string): string => value ? value.replace(/[_.]/g, " ") : "-";
